// ============================================================================
// JARVISTRAVEL - ALERT STATE
// Which trip alerts have been shown as a card, and which have been read in the
// bell. Per trip, in browser storage.
//
// JAR-2552. Dismissal used to be `useState` inside AlertBanner, reset whenever
// the trip changed. It died on every unmount, so closing a card bought you
// until the next render that remounted the banner, and Brent's "Trending over"
// came back every time. Nothing was ever written down.
//
// The `jt.` prefix is the storage-ownership convention: localDataPurge sweeps
// `jarvis_`, `jarvis.` and `jt.` on sign-out and on delete-account, so these
// keys are already erased with everything else and the next person to use the
// browser does not inherit them. That is why there is no entry to add there.
//
// SCOPE: this is per device. Dismiss on the phone and the laptop still shows
// the card once, because the laptop has its own storage. The acceptance asks
// for a reload, which this satisfies exactly. Following someone between their
// devices needs core to hold per-alert read state, which is a real piece of
// work and not this one.
// ============================================================================

const KEY_PREFIX = 'jt.alerts.';

const keyFor = (tripId: string) => `${KEY_PREFIX}${tripId}`;

/**
 * What is remembered about one trip's alerts.
 *
 * `seen` is when an alert was first SHOWN as a card, not when it was closed.
 * That is the distinction the ticket turns on: Brent asked for each alert to
 * appear once and then live in the bell, so simply reading it is enough to
 * retire the card. Closing it with the X is the same event arriving sooner.
 *
 * `read` is whether it has been opened in the bell, which is what the red dot
 * reflects.
 *
 * `dismissed` is the X ON THE BELL ROW: the person has dealt with this one and
 * does not want it listed any more. Separate from `read`, because reading a
 * thing and being finished with it are different, and separate from the card's
 * own X, which only ends that card early (JAR-2552 review, H1).
 */
export interface TripAlertState {
  seen: Record<string, string>;
  read: Record<string, true>;
  dismissed: Record<string, true>;
}

// A FUNCTION, not a shared constant. readAlertState used to hand one module
// level default object to every caller with no stored record, so a caller that
// wrote into the result — `state.seen[id] = …` instead of going through
// markAlertsSeen — would corrupt that default for every later read in the page
// (JAR-2552 review, 10-05).
const empty = (): TripAlertState => ({ seen: {}, read: {}, dismissed: {} });

/**
 * readAlertState returns what is stored for a trip.
 *
 * Every access is guarded. Storage throws in a private window, when site data
 * is blocked, and during a thumbnail capture; an alert card is not worth taking
 * the dashboard down for, so a failure reads as "nothing remembered" and the
 * card shows once more. Showing an alert twice is a smaller harm than a blank
 * screen.
 */
export function readAlertState(tripId: string): TripAlertState {
  try {
    const raw = localStorage.getItem(keyFor(tripId));
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<TripAlertState>;
    // Written by an older or a corrupted copy: take what is usable rather than
    // throwing away the whole record or trusting its shape.
    return {
      seen: isStringMap(parsed.seen) ? parsed.seen : {},
      read: isTrueMap(parsed.read) ? parsed.read : {},
      // Absent in records written before the X on a bell row existed, which is
      // why this reads as {} rather than failing the whole record.
      dismissed: isTrueMap(parsed.dismissed) ? parsed.dismissed : {},
    };
  } catch {
    return empty();
  }
}

function write(tripId: string, state: TripAlertState): void {
  try {
    localStorage.setItem(keyFor(tripId), JSON.stringify(state));
  } catch {
    // Full, blocked or private. The alert shows again next load, which is the
    // old behaviour and is survivable.
  }
}

/** markAlertsSeen records that these alerts have now had their one card. */
export function markAlertsSeen(tripId: string, ids: readonly string[], now = new Date()): TripAlertState {
  const state = readAlertState(tripId);
  let changed = false;
  const seen = { ...state.seen };
  for (const id of ids) {
    // Never overwrite an existing timestamp: the first showing is the one that
    // matters, and rewriting it on every render would make the bell's ordering
    // jump around as the dashboard re-rendered.
    if (!seen[id]) {
      seen[id] = now.toISOString();
      changed = true;
    }
  }
  if (!changed) return state;
  const next = { ...state, seen };
  write(tripId, next);
  return next;
}

/** markAlertRead clears the red dot for one alert. */
export function markAlertRead(tripId: string, id: string): TripAlertState {
  const state = readAlertState(tripId);
  if (state.read[id]) return state;
  const next = { ...state, read: { ...state.read, [id]: true as const } };
  write(tripId, next);
  return next;
}

/**
 * markAlertDismissed takes one alert off the bell for good.
 *
 * Local only. Core has never heard of these ids, so the bell's ordinary
 * dismiss — which posts to core — answered 404 for every alert row, rolled
 * back, and put the row straight back (JAR-2552 review, H1).
 */
export function markAlertDismissed(tripId: string, id: string): TripAlertState {
  const state = readAlertState(tripId);
  if (state.dismissed[id]) return state;
  // Dismissing is also reading it: a row taken off the bell must not leave the
  // red dot lit for something no longer listed.
  const next = {
    ...state,
    read: { ...state.read, [id]: true as const },
    dismissed: { ...state.dismissed, [id]: true as const },
  };
  write(tripId, next);
  return next;
}

/** forgetAlerts drops a trip's record, for a deleted trip or a test. */
export function forgetAlerts(tripId: string): void {
  try {
    localStorage.removeItem(keyFor(tripId));
  } catch {
    // Nothing to do, and nothing worth failing for.
  }
}

function isStringMap(v: unknown): v is Record<string, string> {
  return !!v && typeof v === 'object' && Object.values(v).every((x) => typeof x === 'string');
}

function isTrueMap(v: unknown): v is Record<string, true> {
  return !!v && typeof v === 'object' && Object.values(v).every((x) => x === true);
}
